import { useState, useEffect, useRef, useCallback } from "react";

const WHATSAPP_LINK = "https://whatsapp.com/channel/0029VbDBcHvFnSz8dDN9mP46";
const STORAGE_KEY = "mad_whatsapp_popup_dismissed";
const DISMISS_DURATION_HOURS = 24;
const TOOLTIP_FIRST_VISIT_KEY = "mad_wsa_tooltip_seen";
const TOOLTIP_SHOW_DURATION = 4000; // 4 seconds
const TOOLTIP_INTERVAL = 120000; // 2 minutes

export const WhatsAppFloat = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerTooltip = useCallback(() => {
    setShowTooltip(true);
    // Auto-hide after duration
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, TOOLTIP_SHOW_DURATION);
  }, []);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setIsVisible(true), 1500);

    // Check if first visit (no localStorage key)
    const hasSeen = localStorage.getItem(TOOLTIP_FIRST_VISIT_KEY);
    if (!hasSeen) {
      // First visit: show tooltip after button appears
      tooltipTimerRef.current = setTimeout(() => {
        triggerTooltip();
        localStorage.setItem(TOOLTIP_FIRST_VISIT_KEY, "true");
      }, 2500);
    } else {
      // Returning user: show tooltip after interval
      tooltipTimerRef.current = setTimeout(() => {
        triggerTooltip();
      }, TOOLTIP_INTERVAL);
    }

    // Set up recurring interval every 2 minutes
    intervalRef.current = setInterval(() => {
      triggerTooltip();
    }, TOOLTIP_INTERVAL);

    return () => {
      clearTimeout(fadeTimer);
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [triggerTooltip]);

  if (!isVisible) return null;

  return (
    <>
      <style>{`
        @keyframes wsa-wiggle {
          0%, 92%, 100% { transform: translateX(0); }
          93% { transform: translateX(2.5px); }
          94% { transform: translateX(-2.5px); }
          95% { transform: translateX(2px); }
          96% { transform: translateX(-1.5px); }
          97% { transform: translateX(1px); }
          98% { transform: translateX(0); }
        }
        @keyframes wsa-badge-pulse {
          0%, 100% { transform: scale(1); }
          15% { transform: scale(1.22); }
          30% { transform: scale(1); }
        }
        @keyframes wsa-fade-in {
          0% { opacity: 0; transform: translateY(12px) scale(0.85); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes wsa-tooltip-in {
          0% { opacity: 0; transform: translateX(8px) scale(0.9); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes wsa-tooltip-out {
          0% { opacity: 1; transform: translateX(0) scale(1); }
          100% { opacity: 0; transform: translateX(8px) scale(0.9); }
        }
        .wsa-float {
          animation: wsa-fade-in 0.4s ease-out forwards, wsa-wiggle 7s ease-in-out infinite;
        }
        .wsa-badge {
          animation: wsa-badge-pulse 3s ease-in-out infinite;
        }
        .wsa-tooltip-enter {
          animation: wsa-tooltip-in 0.3s ease-out forwards;
        }
        .wsa-tooltip-exit {
          animation: wsa-tooltip-out 0.25s ease-in forwards;
        }
        .wsa-glow {
          box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.5);
          transition: box-shadow 0.35s ease, transform 0.25s ease;
        }
        .wsa-glow:hover {
          box-shadow: 0 0 18px 4px rgba(37, 211, 102, 0.45), 0 0 32px 6px rgba(37, 211, 102, 0.2);
          transform: scale(1.1);
        }
      `}</style>

      <div className="fixed right-4 sm:right-6 z-50 flex items-center gap-3" style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}>
        {/* Tooltip */}
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className={`relative ${showTooltip ? "wsa-tooltip-enter" : "wsa-tooltip-exit opacity-0 pointer-events-none"}`}
          style={{ animationDuration: showTooltip ? "0.3s" : "0.25s" }}
        >
          <div className="bg-foreground-900/90 backdrop-blur-sm text-white text-sm font-medium px-4 py-2.5 rounded-2xl rounded-br-md shadow-lg whitespace-nowrap cursor-pointer hover:bg-foreground-900 transition-colors">
            How can we help?
            {/* Arrow pointing to the button */}
            <div className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-foreground-900/90 rotate-45 rounded-br-sm" />
          </div>
        </a>

        {/* WhatsApp Button */}
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="wsa-float wsa-glow w-14 h-14 flex items-center justify-center bg-whatsapp-500 hover:bg-whatsapp-600 text-white rounded-full cursor-pointer flex-shrink-0"
          aria-label="Join our WhatsApp channel"
        >
          <i className="ri-whatsapp-line text-2xl"></i>
          {/* Notification badge */}
          <span className="wsa-badge absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-[10px] font-extrabold leading-none rounded-full select-none">
            !
          </span>
        </a>
      </div>
    </>
  );
};

export const WhatsAppPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tooltipHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerPopupTooltip = useCallback(() => {
    setShowTooltip(true);
    if (tooltipHideRef.current) clearTimeout(tooltipHideRef.current);
    tooltipHideRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, TOOLTIP_SHOW_DURATION);
  }, []);

  useEffect(() => {
    const dismissedRaw = sessionStorage.getItem(STORAGE_KEY);
    if (dismissedRaw) {
      const dismissedAt = parseInt(dismissedRaw, 10);
      const hoursPassed = (Date.now() - dismissedAt) / (1000 * 60 * 60);
      if (hoursPassed < DISMISS_DURATION_HOURS) return;
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 6000);
    return () => clearTimeout(timer);
  }, []);

  // Start tooltip cycle when popup opens
  useEffect(() => {
    if (!isOpen) return;

    const hasSeen = localStorage.getItem(TOOLTIP_FIRST_VISIT_KEY);
    const firstDelay = hasSeen ? 2000 : 500; // quicker if first visit, since they're already engaged

    const initTimer = setTimeout(() => {
      triggerPopupTooltip();
    }, firstDelay);

    tooltipIntervalRef.current = setInterval(() => {
      triggerPopupTooltip();
    }, TOOLTIP_INTERVAL);

    return () => {
      clearTimeout(initTimer);
      if (tooltipIntervalRef.current) clearInterval(tooltipIntervalRef.current);
      if (tooltipHideRef.current) clearTimeout(tooltipHideRef.current);
    };
  }, [isOpen, triggerPopupTooltip]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center p-4 transition-opacity duration-300 ${
        isClosing ? "opacity-0" : "opacity-100"
      }`}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      <div
        className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 transition-all duration-300 ${
          isClosing ? "scale-95 translate-y-4" : "scale-100 translate-y-0"
        }`}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-600 hover:bg-foreground-100 transition-colors cursor-pointer"
          aria-label="Close popup"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-whatsapp-50 rounded-full">
            <i className="ri-whatsapp-line text-whatsapp-500 text-3xl"></i>
          </div>
          <h3 className="text-lg font-bold text-foreground-900 mb-2">
            Join Our WhatsApp Channel
          </h3>
          <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
            Get updates on new data packages, exclusive deals, and promotions. Join thousands of happy customers on our WhatsApp channel.
          </p>

          {/* Popup tooltip nudge */}
          <div
            className={`mb-4 transition-all duration-300 ${
              showTooltip ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
            }`}
          >
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDismiss}
              className="inline-flex items-center gap-2 bg-whatsapp-500/10 text-whatsapp-600 text-sm font-semibold px-4 py-2.5 rounded-full cursor-pointer hover:bg-whatsapp-500/20 transition-colors"
            >
              <i className="ri-question-answer-line text-base"></i>
              How can we help?
              <i className="ri-arrow-right-line text-base"></i>
            </a>
          </div>

          <div className="space-y-3">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDismiss}
              className="flex items-center justify-center gap-2 w-full bg-whatsapp-500 hover:bg-whatsapp-600 text-white font-bold text-sm py-3.5 rounded-xl transition-all hover:shadow-lg hover:scale-[1.02] cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Join WhatsApp Channel
            </a>
            <button
              onClick={handleDismiss}
              className="w-full text-sm font-medium text-foreground-400 hover:text-foreground-600 py-2 transition-colors cursor-pointer"
            >
              Maybe later
            </button>
          </div>

          <div className="mt-5 pt-4 border-t border-foreground-100 flex items-center justify-center gap-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-check-line text-whatsapp-500"></i> Data deals
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-check-line text-whatsapp-500"></i> Weekly updates
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-check-line text-whatsapp-500"></i> No spam
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};